import { Card, H2 } from '@blueprintjs/core';
import { AddLarge, Renew } from '@carbon/icons-react';
import * as React from 'react';
import {
  Body,
  Button,
  Loading,
  SystemMessage,
} from 'vet-bones/design-system/components';

import { useAppDispatch } from 'src/ui/app/hooks';
import {
  MANAGE_API_TOKENS_USER_CREATE,
  MANAGE_API_TOKENS_USER_CREATE_DISABLED,
  MANAGE_API_TOKENS_USER_DESCRIPTION,
  MANAGE_API_TOKENS_USER_EMPTY,
  MANAGE_API_TOKENS_USER_ERROR,
  MANAGE_API_TOKENS_USER_ERROR_RETRY,
  MANAGE_API_TOKENS_USER_ERROR_TOKENS_STILL_WORK,
  MANAGE_API_TOKENS_USER_LOADING,
  MANAGE_API_TOKENS_USER_TABLE,
} from 'src/ui/constants/testIds';
import { ApiTokensTable } from 'src/ui/containers/api-tokens/ApiTokenTable';
import { showCreateApiTokenDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { ApiToken, useListApiTokensQuery } from 'src/ui/graph/types';
import * as copy from 'src/ui/templates/copy';
import { isApiTokensEnabled } from 'src/ui/utils/features';

export const UserApiTokens: React.VFC = () => {
  const dispatch = useAppDispatch();

  const {
    data,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useListApiTokensQuery(graphQLClient);

  const apiTokens =
    data?.listApiTokens?.filter((x): x is ApiToken => Boolean(x)) || [];
  // When the public API does not accept user tokens there is nothing to mint,
  // but existing tokens stay listed so they can still be revoked.
  const canCreate = isApiTokensEnabled();

  let list: React.ReactNode;
  if (isLoading || (isError && isFetching && !data)) {
    // Covers the first load and a retry after a failure with nothing to
    // show meanwhile.
    list = <Loading size="md" testId={MANAGE_API_TOKENS_USER_LOADING} />;
  } else if (isError && !data) {
    // A failed refresh of a list that already loaded keeps showing the list,
    // including while it is being retried.
    list = (
      <div className="sd-api-tokens-error">
        <SystemMessage
          testId={MANAGE_API_TOKENS_USER_ERROR}
          text={copy.components.manageApiTokens.userTokens.error.message}
          variant="alert"
        />
        {canCreate ? (
          <Body
            size="md"
            testId={MANAGE_API_TOKENS_USER_ERROR_TOKENS_STILL_WORK}
          >
            {copy.components.manageApiTokens.userTokens.error.tokensStillWork}
          </Body>
        ) : null}
        <Button
          leftIcon={Renew}
          onClick={() => refetch()}
          size="sm"
          testId={MANAGE_API_TOKENS_USER_ERROR_RETRY}
          variant="secondary-solid"
        >
          {copy.components.manageApiTokens.userTokens.error.retry}
        </Button>
      </div>
    );
  } else if (apiTokens.length) {
    list = (
      <Card>
        <ApiTokensTable apiTokens={apiTokens} />
      </Card>
    );
  } else {
    list = (
      <Body size="md" testId={MANAGE_API_TOKENS_USER_EMPTY}>
        {copy.components.manageApiTokens.userTokens.empty}
      </Body>
    );
  }

  return (
    <div className="sd-table" data-testid={MANAGE_API_TOKENS_USER_TABLE}>
      <div className="sd-table__header">
        <H2>{copy.components.manageApiTokens.userTokens.header}</H2>
        {canCreate ? (
          <div className="sd-table__actions">
            <div className="sd-table__actions__button-container">
              <Button
                leftIcon={AddLarge}
                onClick={() => dispatch(showCreateApiTokenDialog({}))}
                size="sm"
                testId={MANAGE_API_TOKENS_USER_CREATE}
                variant="tertiary-accent"
              >
                {copy.components.manageApiTokens.userTokens.add}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
      <Body size="md" testId={MANAGE_API_TOKENS_USER_DESCRIPTION}>
        {copy.components.manageApiTokens.userTokens.description}
      </Body>
      {canCreate ? null : (
        <SystemMessage
          testId={MANAGE_API_TOKENS_USER_CREATE_DISABLED}
          text={copy.components.manageApiTokens.userTokens.creationDisabled}
          variant="info"
        />
      )}
      <hr className="divider" />
      {list}
    </div>
  );
};
